import { createAsyncThunk } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { graphqlRequest, hasGraphQLNetworkError } from '~/adapters/graphqlRequest';
import {
  AddSurveyElementDocument,
  AddSurveyElementMutationResult,
  AddTrendQuestionDocument,
  AddTrendQuestionMutationResult,
  DeleteSurveyElementDocument,
  DeleteSurveyElementMutationResult,
  GetLibraryQuestionsDocument,
  GetLibraryQuestionsQueryResult,
  GetTrendQuestionsDocument,
  GetTrendQuestionsQueryResult,
  OrderSurveyElementsDocument,
  OrderSurveyElementsMutationResult,
  OpenSurveyAttributeInput,
  UpdateSurveyElementDocument,
} from '~/gql/graphql';
import * as transformers from '../transformers';
import * as schemas from '../schemas';
import { surveyActions } from './survey.actions';
import { list } from '~/tools/list';
import { Survey, SurveyElement } from '~/interfaces/survey.interface';
import { AppDispatch, RootState } from '../store';
import { QueryQuestionsParams } from '~/interfaces/survey-element.interface';
import { fetchSurveyElementsBySurveysQuery } from '../react-query/surveyElements';

type Language = {
  langISOCode: string;
};

type MessagePerLanguage = {
  language: Language;
  message: string;
};

type Choice = {
  orderIndex: number;
  messagesPerLanguage: MessagePerLanguage[];
};

type DynamicSurveyElementTranslation = {
  [key: string]: string;
};

type StaticSurveyElementTranslation = {
  title: string;
  desc: string;
  minLabel: string | null;
  maxLabel: string | null;
};

type SurveyElementTranslation = DynamicSurveyElementTranslation & StaticSurveyElementTranslation;

export type ParseableSurveyElementInput = {
  id: number | null;
  orderIndex: number;
  typeName: string;
  categoryId: number;
  isRequired: boolean;
  allowComments: boolean;
  showSkipButton: boolean;
  showLabels: boolean;
  showDescription: boolean;
  hideReporting: boolean;
  isLibraryQuestion: boolean;
  copiedLibraryId: number | null;
  isAttributeBased: boolean;
  /** Undefined leaves the saved configuration alone; null removes it. */
  openSurveyAttribute?: OpenSurveyAttributeInput | null;
  selectedAttributes: Record<string, string[]> | null;
  iconName: string | null;
  minOptions: number | null;
  maxOptions: number | null;
  minSelectedOption: number;
  maxSelectedOption: number;
  translations: Record<string, SurveyElementTranslation>;
  totalOptions: number;
  isDiscontinued: boolean;
};

export type SurveyElementOutput = {
  id: number | null;
  isLibraryQuestion: boolean;
  copiedLibraryId: number | null;
  isAttributeBased: boolean;
  orderIndex: number;
  ratingOptionLowest: number | null;
  ratingOptionHighest: number | null;
  icon: { name: string } | null;
  required: boolean;
  showTextComment: boolean;
  showSkipButton: boolean;
  showLabels: boolean;
  showDescription: boolean;
  hideReporting: boolean;
  isDiscontinued?: boolean;
  minSelectedOption: number;
  maxSelectedOption: number;
  category: { id: number } | undefined;
  type: { name: string } | undefined;
  messagesPerLanguage: {
    language: { langISOCode: string };
    message: string;
    description: string;
    labelBefore: string | null;
    labelAfter: string | null;
  }[];
  attributes: { attributeId: number; attributeValue: string }[] | null;
  choices: Choice[] | null;
};

const parseSurveyElement = (
  surveyElement: ParseableSurveyElementInput,
  isCreateSurveyElement = false,
): SurveyElementOutput => {
  const {
    id,
    orderIndex,
    typeName,
    categoryId,
    isRequired,
    allowComments,
    showSkipButton,
    showLabels,
    showDescription,
    hideReporting,
    isLibraryQuestion,
    copiedLibraryId,
    isAttributeBased,
    selectedAttributes,
    iconName,
    minOptions,
    maxOptions,
    minSelectedOption,
    maxSelectedOption,
    translations,
    totalOptions,
    isDiscontinued,
  } = surveyElement;
  let choices: Choice[] | null = [];

  if (!totalOptions) {
    choices = null;
  } else {
    choices = Array(totalOptions)
      .fill(null)
      .map((number, index) => ({
        orderIndex: index + 1,
        messagesPerLanguage: Object.keys(translations).map((langCode) => {
          const translation = translations[langCode];
          return {
            language: { langISOCode: langCode },
            message: translation[`option${index + 1}`],
          };
        }),
      }));
  }

  return {
    id,
    isLibraryQuestion,
    copiedLibraryId,
    isAttributeBased,
    orderIndex,
    ratingOptionLowest: minOptions,
    ratingOptionHighest: maxOptions,
    icon: iconName ? { name: iconName } : null,
    required: isRequired,
    showTextComment: allowComments,
    showSkipButton,
    showLabels,
    showDescription,
    hideReporting,
    isDiscontinued: isCreateSurveyElement ? undefined : isDiscontinued,
    minSelectedOption,
    maxSelectedOption,
    category: categoryId ? { id: categoryId } : undefined,
    type: isCreateSurveyElement ? { name: typeName } : undefined,
    messagesPerLanguage:
      translations &&
      Object.keys(translations).map((langCode) => {
        const translation = translations[langCode];
        return {
          language: { langISOCode: langCode },
          message: translation.title,
          description: translation.desc,
          labelBefore: showLabels ? translation.minLabel : null,
          labelAfter: showLabels ? translation.maxLabel : null,
        };
      }),
    attributes: selectedAttributes
      ? Object.keys(selectedAttributes).reduce(
          (attrs, attrId) => {
            selectedAttributes[attrId].forEach((attrValue) => {
              attrs.push({
                attributeId: Number(attrId),
                attributeValue: attrValue,
              });
            });
            return attrs;
          },
          [] as { attributeId: number; attributeValue: string }[],
        )
      : null,
    choices,
  };
};

const createSurveyElement = createAsyncThunk<
  { surveyId: number; surveyElementId: number; [surveyElementId: number]: SurveyElement },
  { surveyId: number; surveyElement: ParseableSurveyElementInput }
>('surveyElement/createSurveyElement', async ({ surveyId, surveyElement }, { rejectWithValue }) => {
  const isCreateSurveyElement = true;
  const mutationParams = parseSurveyElement(surveyElement, isCreateSurveyElement);
  const res = await graphqlRequest<AddSurveyElementMutationResult>(AddSurveyElementDocument, {
    surveyId,
    surveyElement: { ...mutationParams, openSurveyAttribute: surveyElement.openSurveyAttribute },
  });

  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

  const result = transformers.surveyElementFrom(res.data?.addSurveyElement);
  const normalizeSurveyElement = normalize([result], [schemas.surveyElement]);

  return {
    ...normalizeSurveyElement,
    surveyId,
    surveyElementId: result.id,
  };
});

const addTrendQuestions = createAsyncThunk<
  void,
  { surveyId: number; surveyElementId: number; orderIndex: number },
  { dispatch: AppDispatch }
>(
  'surveyElement/addTrendQuestion',
  // @ts-ignore @TODO Epic WT-21600
  async ({ surveyId, surveyElementId, orderIndex }, { rejectWithValue, dispatch }) => {
    const res = await graphqlRequest<AddTrendQuestionMutationResult>(AddTrendQuestionDocument, {
      surveyId,
      surveyElementId,
      orderIndex,
    });

    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

    // @ts-ignore @TODO Epic WT-21600
    return dispatch(surveyActions.fetchSurveyWithSurveyElements({ surveyId }));
  },
);

const updateSurveyElement = createAsyncThunk<
  { surveyElementId: number; [surveyElementId: number]: SurveyElement },
  { surveyId: number; surveyElement: ParseableSurveyElementInput },
  { state: RootState }
>('surveyElement/updateSurveyElement', async ({ surveyId, surveyElement }, { rejectWithValue, getState }) => {
  const mutationParams = parseSurveyElement(surveyElement);
  const res = await graphqlRequest<any>(UpdateSurveyElementDocument, {
    surveyId,
    surveyElement: {
      action: 'UPDATE',
      surveyElement: mutationParams,
      openSurveyAttribute: surveyElement.openSurveyAttribute,
    },
  });

  const state = getState();
  const survey = state?.entities?.surveys?.[surveyId];

  const surveyLanguageCodes = survey?.languageCodes;

  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

  const result = transformers.surveyElementFrom(res.data?.updateSurveyElement, surveyLanguageCodes);
  const normalizeSurveyElement = normalize([result], [schemas.surveyElement]);

  return {
    ...normalizeSurveyElement,
    surveyElementId: res.data?.updateSurveyElement.id,
  };
});

const deleteSurveyElement = createAsyncThunk<
  { surveyId: number; surveyElementId: number } | unknown,
  { surveyId: number; surveyElementId: number | null }
>('surveyElement/deleteSurveyElement', async ({ surveyId, surveyElementId }, { rejectWithValue }) => {
  const res = await graphqlRequest<DeleteSurveyElementMutationResult>(DeleteSurveyElementDocument, {
    surveyId,
    surveyElementId,
  });

  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

  return {
    surveyId,
    surveyElementId,
  };
});

const orderSurveyElements = createAsyncThunk<
  { surveyElements: SurveyElement[] },
  { surveyId: number; surveyElements: SurveyElement[] }
>('surveyElement/orderSurveyElements', async ({ surveyId, surveyElements }, { rejectWithValue, dispatch }) => {
  const questionOrders = surveyElements.map((surveyElement) => ({
    action: 'UPDATE',
    surveyElement: {
      id: surveyElement.id,
      orderIndex: surveyElement.orderIndex,
    },
  }));

  const res = await graphqlRequest<OrderSurveyElementsMutationResult>(OrderSurveyElementsDocument, {
    id: surveyId,
    survey: { questions: questionOrders },
  });

  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

  return {
    surveyElements,
  };
});

const getLibraryQuestions = createAsyncThunk<
  { total: number; surveyElementIds: number[]; [surveyElementId: number]: SurveyElement },
  QueryQuestionsParams,
  { state: RootState }
>(
  'surveyElement/getLibraryQuestions',
  async (
    {
      limit,
      offset,
      companyId,
      surveyId,
      languageIds,
      filteredCategories,
      questionTitle,
      searchLanguageId,
      surveyTypeId,
    },
    { rejectWithValue, getState },
  ) => {
    const state = getState();
    const categories = state.entities.categories;
    const sortedCats: any[] = Object.values(categories).sort(list.byName as any);
    const categoryIds = sortedCats.map((key) => key.id);

    const search = {
      companyId,
      surveyId,
      languageIds,
      filteredCategories: filteredCategories || categoryIds,
      questionTitle,
      searchLanguageId,
      surveyTypeId,
    };

    const res = await graphqlRequest<GetLibraryQuestionsQueryResult>(GetLibraryQuestionsDocument, {
      limit,
      offset,
      search,
    });

    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

    const surveyElements = transformers.surveyElementsFrom(res.data?.libraryQuestions?.fields);
    const normalizedData = normalize(surveyElements, [schemas.surveyElement]);

    return {
      ...normalizedData,
      total: res.data?.libraryQuestions?.count as number,
      surveyElementIds: normalizedData.result,
    };
  },
);

const getTrendQuestions = createAsyncThunk<
  { total: number; surveyElementIds: number[]; [surveyElementId: number]: SurveyElement },
  QueryQuestionsParams
>(
  'surveyElement/getTrendQuestions',
  async (
    { limit, offset, companyId, surveyId, surveyTypeId, languageIds, categoryId, questionTitle, searchLanguageId },
    { rejectWithValue },
  ) => {
    const search = {
      companyId,
      surveyId,
      surveyTypeId,
      languageIds,
      categoryId: categoryId || undefined,
      questionTitle,
      searchLanguageId,
    };

    const res = await graphqlRequest<GetTrendQuestionsQueryResult>(GetTrendQuestionsDocument, {
      limit,
      offset,
      search,
    });

    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

    const surveyElements = transformers.surveyElementsFrom(res.data?.previousQuestions?.fields);
    const surveyElementIds = surveyElements.map((se) => se.id);

    const normalizedData = normalize(surveyElements, [schemas.surveyElement]);

    return {
      ...normalizedData,
      total: res.data?.previousQuestions?.count as number,
      surveyElementIds,
    };
  },
);

const fetchSurveyElementsBySurveys = createAsyncThunk<{ [surveyId: number]: Survey }, { surveyIds: number[] }>(
  'surveyElement/fetchSurveyElementsBySurveys',
  async ({ surveyIds }) => {
    const surveys = await fetchSurveyElementsBySurveysQuery(surveyIds);

    const normalizedData = normalize(surveys, [schemas.survey]);
    return normalizedData;
  },
);

export const surveyElementActions = {
  createSurveyElement,
  updateSurveyElement,
  deleteSurveyElement,
  orderSurveyElements,
  getLibraryQuestions,
  getTrendQuestions,
  addTrendQuestions,
  fetchSurveyElementsBySurveys,
};
